import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { ShieldCheck, KeyRound, LockKeyhole } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/* Connexion console admin — URL dédiée, jamais référencée dans la navigation.
   Inaccessible depuis l'app mobile publiée (Capacitor natif). */
export default function AdminLogin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState(null); // { challenge_id }
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (Capacitor.isNativePlatform()) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-mute" data-testid="admin-login-native-blocked">
        Page introuvable
      </div>
    );
  }

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/login", {
        email: form.email.trim(), password: form.password, space: "admin",
      });
      if (data?.totp_required) {
        setChallenge({ challenge_id: data.challenge_id });
      } else {
        setUser(data);
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/admin/totp", {
        challenge_id: challenge.challenge_id, code: code.trim(),
      });
      setUser(data);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-ink text-white grid place-items-center">
            <LockKeyhole className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold">Console d'administration</h1>
            <p className="text-xs text-mute">Accès réservé — NelDaari</p>
          </div>
        </div>

        {!challenge ? (
          <form onSubmit={submitPassword} className="bg-white border border-sand rounded-2xl p-5 space-y-3">
            <input
              type="email" required autoComplete="username" placeholder="Email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-login-email"
              className="w-full rounded-xl border border-sand px-4 py-3 text-sm focus:outline-none focus:border-ink"
            />
            <input
              type="password" required autoComplete="current-password" placeholder="Mot de passe"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="admin-login-password"
              className="w-full rounded-xl border border-sand px-4 py-3 text-sm focus:outline-none focus:border-ink"
            />
            {error && <div className="text-sm text-brand-red" data-testid="admin-login-error">{error}</div>}
            <button
              type="submit" disabled={loading} data-testid="admin-login-submit"
              className="w-full rounded-xl bg-ink hover:bg-black text-white py-3 font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <ShieldCheck className="w-4 h-4" /> {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="bg-white border border-sand rounded-2xl p-5 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-brand-red" /> Double authentification
            </div>
            <p className="text-xs text-mute">
              Saisissez le code à 6 chiffres de votre application d'authentification,
              ou un code de secours (XXXX-XXXX).
            </p>
            <input
              type="text" required autoFocus inputMode="text" placeholder="123456"
              value={code} onChange={(e) => setCode(e.target.value)}
              data-testid="admin-totp-code"
              className="w-full rounded-xl border border-sand px-4 py-3 text-center text-lg tracking-[0.3em] font-mono focus:outline-none focus:border-ink"
            />
            {error && <div className="text-sm text-brand-red" data-testid="admin-totp-error">{error}</div>}
            <button
              type="submit" disabled={loading} data-testid="admin-totp-submit"
              className="w-full rounded-xl bg-ink hover:bg-black text-white py-3 font-semibold disabled:opacity-60"
            >
              {loading ? "Vérification…" : "Vérifier"}
            </button>
            <button
              type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }}
              data-testid="admin-totp-back"
              className="w-full text-xs text-mute hover:text-ink"
            >
              Retour
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
